import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import { Box, VStack, Text, Button, useToast, Flex, Image } from '@chakra-ui/react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';

/**
 * A component that protects routes requiring wallet connection
 * If wallet is not connected, it shows a message and connect wallet button
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render when authenticated
 * @param {boolean} props.requireWallet - Whether to strictly require wallet connection (default: true)
 */
const ProtectedRoute = ({ children, requireWallet = true }) => {
    const { connected } = useWallet();
    const { setVisible } = useWalletModal();
    const location = useLocation();
    const toast = useToast();
    const [isPasswordAuthenticated, setIsPasswordAuthenticated] = useState(false);
    
    // Check for password authentication in localStorage
    useEffect(() => {
        const checkPasswordAuth = () => {
            const passwordAuth = localStorage.getItem('palmHeistPasswordAuth');
            console.log('Password auth from localStorage:', passwordAuth);
            setIsPasswordAuthenticated(passwordAuth === 'true');
        };
        
        // Check immediately
        checkPasswordAuth();
        
        // Also set up an event listener for storage changes
        window.addEventListener('storage', checkPasswordAuth);
        
        return () => {
            window.removeEventListener('storage', checkPasswordAuth);
        };
    }, []);

    // Show toast when component mounts if wallet is not connected and no password auth
    useEffect(() => {
        if (requireWallet && !connected && !isPasswordAuthenticated) {
            toast({
                title: "Authentication Required",
                description: "Please connect your wallet or use a password to access this page",
                status: "warning",
                duration: 5000,
                isClosable: true,
                position: "top",
            });
        }
    }, [connected, toast, requireWallet, isPasswordAuthenticated]);

    const handleConnectWallet = () => {
        setVisible(true);
    };

    // If connected, password authenticated, or wallet not strictly required, render the children components
    if (connected || isPasswordAuthenticated || !requireWallet) {
        return children;
    }

    // If wallet is required but not connected, show the wallet connection screen
    return (
        <Box 
            w="100%" 
            h="100vh" 
            display="flex" 
            alignItems="center" 
            justifyContent="center"
            bg="linear-gradient(to bottom, #0f0f0f, #1a1a1a)"
            color="white"
            position="relative"
            overflow="hidden"
        >
            {/* Background particles effect */}
            <Box
                position="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
                opacity="0.2"
                zIndex="0"
                backgroundImage="url('/images/particles-bg.png')"
                backgroundSize="cover"
                backgroundPosition="center"
            />
            
            <VStack spacing={6} zIndex="1" textAlign="center" p={4}>
                <Text fontSize="3xl" fontWeight="bold" mb={2}>
                    Authentication Required
                </Text>
                <Text fontSize="lg" maxW="600px" mb={4}>
                    You need to connect your wallet or use a password to access this area of Palm Heist.
                </Text>
                
                <Button
                    onClick={handleConnectWallet}
                    size="lg"
                    bg="#00E676"
                    color="black"
                    _hover={{ bg: "#00C853" }}
                    rounded="full"
                    px={8}
                    py={6}
                    fontSize="lg"
                    fontWeight="bold"
                    className="connect-wallet-button-pulse"
                >
                    Connect Wallet
                </Button>
                
                {/* Add pulse animation for the button */}
                <style jsx global>{`
                    @keyframes pulse {
                        0% { box-shadow: 0 0 0 0 rgba(0, 230, 118, 0.7); }
                        70% { box-shadow: 0 0 0 15px rgba(0, 230, 118, 0); }
                        100% { box-shadow: 0 0 0 0 rgba(0, 230, 118, 0); }
                    }
                    .connect-wallet-button-pulse {
                        animation: pulse 2s infinite;
                    }
                `}</style>
                
                <Flex mt={8} gap={4} flexWrap="wrap" justifyContent="center">
                    <Box textAlign="center" p={2}>
                        <Image src="/images/phantom.png" alt="Phantom" w="40px" h="40px" mx="auto" mb={2} />
                        <Text fontSize="sm">Phantom</Text>
                    </Box>
                    <Box textAlign="center" p={2}>
                        <Image src="/images/solflare.png" alt="Solflare" w="40px" h="40px" mx="auto" mb={2} />
                        <Text fontSize="sm">Solflare</Text>
                    </Box>
                    <Box textAlign="center" p={2}>
                        <Image src="/images/coinbase.png" alt="Coinbase" w="40px" h="40px" mx="auto" mb={2} />
                        <Text fontSize="sm">Coinbase</Text>
                    </Box>
                    <Box textAlign="center" p={2}>
                        <Image src="/images/trust.png" alt="Trust" w="40px" h="40px" mx="auto" mb={2} />
                        <Text fontSize="sm">Trust</Text>
                    </Box>
                </Flex>
            </VStack>
        </Box>
    );
};

export { ProtectedRoute };
export default ProtectedRoute;
